(() => {
  'use strict';

  /* PN_PHASE5_PAYOUT_UI_V1 */
  /* PN_PHASE5_PAYOUT_BUTTON_SUBMIT_FIX_V1 */

  const FUNCTION_BASE =
    'https://tnlkoibetblgiovgkufs.supabase.co/functions/v1';

  let activeMentorId = null;
  let adminTimer = null;
  let mentorHistoryBusy = false;


  const esc = value =>
    String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');


  function money(
    value,
    currency = 'INR'
  ) {

    const number =
      Number(value || 0);

    return new Intl.NumberFormat(
      'en-IN',
      {
        style:'currency',
        currency:
          currency || 'INR',
        minimumFractionDigits:
          number % 1 ? 2 : 0,
        maximumFractionDigits:2
      }
    ).format(number);

  }


  function dateText(value) {

    if (!value) {
      return '—';
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return '—';
    }

    return date.toLocaleString(
      undefined,
      {
        dateStyle:'medium',
        timeStyle:'short'
      }
    );

  }


  function pill(value) {

    const status =
      String(
        value || 'unknown'
      )
        .toLowerCase();

    return `
      <span
        class="pn5-payout-pill"
        data-state="${esc(status)}"
      >
        ${esc(
          status.replaceAll(
            '_',
            ' '
          )
        )}
      </span>
    `;

  }


  async function rpc(
    name,
    body = {}
  ) {

    return window.PeerNestDB
      .request(
        `rpc/${name}`,
        '',
        {
          method:'POST',
          body
        }
      );

  }


  async function edge(
    name,
    body = {}
  ) {

    const getValidSession =
      async () => {

        let session =
          await window.PeerNestDB
            .getSession();


        if (
          !session?.access_token
        ) {

          session =
            await window.PeerNestDB
              .refreshSession?.()
              .catch(
                () => null
              );

        }


        if (
          !session?.access_token
        ) {

          throw new Error(
            'Your admin session expired. Please sign in again.'
          );

        }


        return session;

      };


    const call =
      async accessToken => {

        return fetch(
          `${FUNCTION_BASE}/${name}`,
          {
            method:'POST',

            headers:{
              Authorization:
                `Bearer ${accessToken}`,

              'Content-Type':
                'application/json'
            },

            body:
              JSON.stringify(
                body
              )
          }
        );

      };


    let session =
      await getValidSession();


    let response =
      await call(
        session.access_token
      );


    /*
      PN_PAYOUT_AUTH_RETRY_V1

      Browser session may still contain an access token
      that expired just before the payout request.

      Refresh once and retry. Never loop.
    */
    if (
      response.status ===
      401
      &&
      window.PeerNestDB
        .refreshSession
    ) {

      const refreshed =
        await window.PeerNestDB
          .refreshSession()
          .catch(
            () => null
          );


      if (
        refreshed?.access_token
      ) {

        session =
          refreshed;


        response =
          await call(
            refreshed.access_token
          );

      }

    }


    const data =
      await response
        .json()
        .catch(
          () => ({})
        );


    if (!response.ok) {

      const message =
        data?.error ===
        'unauthorized'
          ? (
              'Your admin session could not be verified. '
              +
              'Sign out and sign in again, then retry.'
            )
          : (
              data?.error
              ||
              `Payout service failed (${response.status}).`
            );


      const error =
        new Error(
          message
        );


      error.data =
        data;

      error.status =
        response.status;


      throw error;

    }


    return data;

  }


  async function adminData(
    mentorId
  ) {

    const [
      account,
      finance,
      payouts
    ] =
      await Promise.all([

        rpc(
          'admin_get_mentor_payout_account_v1',
          {
            p_mentor_id:
              mentorId
          }
        ),

        rpc(
          'admin_get_mentor_finance_detail_v2',
          {
            p_mentor_id:
              mentorId
          }
        ),

        rpc(
          'admin_list_mentor_payouts_v1'
        )

      ]);

    return {
      account:
        account || {
          configured:false
        },

      finance:
        finance || {},

      payouts:
        Array.isArray(payouts)
          ? payouts.filter(
              row =>
                String(
                  row.mentor_id
                ) ===
                String(
                  mentorId
                )
            )
          : []
    };

  }


  function payoutActions(
    payout
  ) {

    const status =
      String(
        payout.status || ''
      )
        .toLowerCase();

    const provider =
      Boolean(
        payout.provider_payout_id
      );


    if (
      status === 'created' &&
      !provider
    ) {

      return `
        <div class="pn5-payout-actions">

          <button
            type="button"
            data-pn5-retry="${esc(
              payout.id
            )}"
          >
            Retry safely
          </button>

          <button
            type="button"
            data-pn5-reconcile="${esc(
              payout.id
            )}"
          >
            Reconcile
          </button>

          <button
            type="button"
            class="is-danger"
            data-pn5-cancel="${esc(
              payout.id
            )}"
          >
            Cancel batch
          </button>

        </div>
      `;

    }


    if (
      [
        'queued',
        'pending',
        'processing'
      ].includes(status)
    ) {

      return `
        <button
          type="button"
          data-pn5-reconcile="${esc(
            payout.id
          )}"
        >
          Reconcile
        </button>
      `;

    }


    if (provider) {

      return `
        <button
          type="button"
          data-pn5-reconcile="${esc(
            payout.id
          )}"
        >
          Refresh provider
        </button>
      `;

    }


    return '—';

  }


  function payoutTable(
    rows
  ) {

    if (!rows.length) {

      return `
        <div class="pn5-payout-empty">
          No payout batches yet.
        </div>
      `;

    }


    return `
      <div class="pn5-payout-table-wrap">

        <table class="pn5-payout-table">

          <thead>
            <tr>
              <th>Date</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Sessions</th>
              <th>Provider ID</th>
              <th>UTR</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>

            ${rows
              .map(
                row => `
                  <tr>

                    <td>
                      ${esc(
                        dateText(
                          row.created_at
                        )
                      )}
                    </td>

                    <td>

                      <strong>
                        ${money(
                          row.amount,
                          row.currency
                        )}
                      </strong>

                      ${
                        Number(
                          row.adjustment_total ||
                          0
                        ) !== 0
                          ? `
                            <small>
                              Adjustment:
                              ${money(
                                row.adjustment_total,
                                row.currency
                              )}
                            </small>
                          `
                          : ''
                      }

                    </td>

                    <td>

                      ${pill(
                        row.status
                      )}

                      ${
                        row.failure_reason
                          ? `
                            <small class="pn5-payout-error">
                              ${esc(
                                row.failure_reason
                              )}
                            </small>
                          `
                          : ''
                      }

                    </td>

                    <td>
                      ${esc(
                        row.earning_count ||
                        0
                      )}
                    </td>

                    <td>

                      ${esc(
                        row.provider_payout_id ||
                        '—'
                      )}

                      <small>
                        ${esc(
                          row.reference_id ||
                          ''
                        )}
                      </small>

                    </td>

                    <td>
                      ${esc(
                        row.provider_utr ||
                        '—'
                      )}
                    </td>

                    <td>
                      ${payoutActions(row)}
                    </td>

                  </tr>
                `
              )
              .join('')}

          </tbody>

        </table>

      </div>
    `;

  }


  function renderAdmin(
    host,
    mentorId,
    data,
    message = ''
  ) {

    const account =
      data.account || {};

    const finance =
      data.finance || {};

    const payouts =
      data.payouts || [];

    const due =
      Number(
        finance.to_be_paid ||
        0
      );

    const configured =
      account.configured ===
      true;


    host.innerHTML = `

      <div class="pn5-payout-head">

        <div>
          <span>RAZORPAYX PAYOUTS</span>
          <h3>Mentor settlement</h3>
          <p>
            Completed earnings only.
            Provider confirmation controls Paid status.
          </p>
        </div>

        <button
          type="button"
          data-pn5-refresh
        >
          Refresh
        </button>

      </div>


      ${
        message
          ? `
            <div class="pn5-payout-message">
              ${esc(message)}
            </div>
          `
          : ''
      }


      <div class="pn5-payout-summary">

        <article>
          <span>TOTAL EARNINGS</span>
          <strong>
            ${money(
              finance.total_earnings
            )}
          </strong>
        </article>

        <article>
          <span>PAID</span>
          <strong>
            ${money(
              finance.paid
            )}
          </strong>
        </article>

        <article>
          <span>TO BE PAID</span>
          <strong>
            ${money(due)}
          </strong>
        </article>

      </div>


      <section class="pn5-payout-account">

        <div class="pn5-payout-subhead">

          <div>
            <span>PAYOUT ACCOUNT</span>
            <h4>RazorpayX beneficiary</h4>
          </div>

          ${
            configured
              ? pill('configured')
              : pill('not_configured')
          }

        </div>


        <div class="pn5-payout-fields">

          <label>

            <span>
              Razorpay Contact ID
            </span>

            <input
              type="text"
              placeholder="cont_..."
              value="${esc(
                account.contact_id ||
                ''
              )}"
              data-pn5-contact
            >

          </label>


          <label>

            <span>
              Razorpay Fund Account ID
            </span>

            <input
              type="text"
              placeholder="fa_..."
              value="${esc(
                account.fund_account_id ||
                ''
              )}"
              data-pn5-fund
            >

          </label>


          <label>

            <span>
              Payout mode
            </span>

            <select data-pn5-mode>

              ${[
                'IMPS',
                'NEFT',
                'RTGS',
                'UPI'
              ]
                .map(
                  mode => `
                    <option
                      value="${mode}"
                      ${
                        String(
                          account.mode ||
                          'IMPS'
                        ) === mode
                          ? 'selected'
                          : ''
                      }
                    >
                      ${mode}
                    </option>
                  `
                )
                .join('')}

            </select>

          </label>


          <label>

            <span>
              Destination hint
            </span>

            <input
              type="text"
              placeholder="HDFC •••• 4821"
              value="${esc(
                account.destination_hint ||
                ''
              )}"
              data-pn5-hint
            >

          </label>

        </div>


        <div class="pn5-payout-buttons">

          <button
            type="button"
            data-pn5-save-account
          >
            Save payout account
          </button>


          <button
            type="button"
            class="is-primary"
            data-pn5-pay
            ${
              !configured ||
              due <= 0
                ? 'disabled'
                : ''
            }
          >
            ${
              due > 0
                ? `Pay Mentor ${money(due)}`
                : 'Nothing to pay'
            }
          </button>

        </div>


        <small class="pn5-payout-security">
          Only Razorpay Contact and Fund Account IDs
          are stored by PeerNest.
        </small>

      </section>


      <section class="pn5-payout-history">

        <div class="pn5-payout-subhead">

          <div>
            <span>PAYOUT HISTORY</span>
            <h4>Provider settlements</h4>
          </div>

        </div>

        ${payoutTable(
          payouts
        )}

      </section>
    `;


    bindAdmin(
      host,
      mentorId
    );

  }


  async function refreshAdmin(
    host,
    mentorId,
    message = ''
  ) {

    host.innerHTML = `
      <div class="pn5-payout-empty">
        Loading payout operations…
      </div>
    `;


    try {

      const data =
        await adminData(
          mentorId
        );


      renderAdmin(
        host,
        mentorId,
        data,
        message
      );

    }
    catch (error) {

      host.innerHTML = `
        <div class="pn5-payout-empty pn5-payout-error">
          ${esc(
            error?.message ||
            'Could not load payout operations.'
          )}
        </div>
      `;

    }

  }


  function bindAdmin(
    host,
    mentorId
  ) {

    host.querySelector(
      '[data-pn5-refresh]'
    )
      ?.addEventListener(
        'click',
        () =>
          refreshAdmin(
            host,
            mentorId
          )
      );


    host.querySelector(
      '[data-pn5-save-account]'
    )
      ?.addEventListener(
        'click',
        async event => {

          const button =
            event.currentTarget;

          button.disabled =
            true;

          button.textContent =
            'Saving…';


          try {

            const contact =
              host.querySelector(
                '[data-pn5-contact]'
              )?.value
                ?.trim() ||
              '';

            const fund =
              host.querySelector(
                '[data-pn5-fund]'
              )?.value
                ?.trim() ||
              '';

            const mode =
              host.querySelector(
                '[data-pn5-mode]'
              )?.value ||
              'IMPS';

            const hint =
              host.querySelector(
                '[data-pn5-hint]'
              )?.value
                ?.trim() ||
              '';


            if (!fund) {

              throw new Error(
                'Razorpay Fund Account ID is required.'
              );

            }


            await rpc(
              'pn_admin_set_mentor_payout_account_v1',
              {
                p_mentor_id:
                  mentorId,

                p_provider_contact_id:
                  contact ||
                  null,

                p_provider_fund_account_id:
                  fund,

                p_payout_mode:
                  mode,

                p_destination_hint:
                  hint ||
                  null
              }
            );


            await refreshAdmin(
              host,
              mentorId,
              'Payout account saved.'
            );

          }
          catch (error) {

            alert(
              error?.message ||
              'Could not save payout account.'
            );

            button.disabled =
              false;

            button.textContent =
              'Save payout account';

          }

        }
      );


    host.querySelector(
      '[data-pn5-pay]'
    )
      ?.addEventListener(
        'click',
        async event => {

          if (
            !window.confirm(
              'Create and submit this mentor payout to RazorpayX?'
            )
          ) {
            return;
          }


          const button =
            event.currentTarget;

          button.disabled =
            true;

          button.textContent =
            'Submitting…';


          try {

            const result =
              await edge(
                'razorpayx-create-payout',
                {
                  mentor_id:
                    mentorId
                }
              );


            await refreshAdmin(
              host,
              mentorId,
              `Payout ${
                result.status ||
                'submitted'
              }.`
            );

          }
          catch (error) {

            alert(
              error?.message ||
              'Could not submit payout.'
            );


            await refreshAdmin(
              host,
              mentorId
            );

          }

        }
      );


    host.querySelectorAll(
      '[data-pn5-retry]'
    )
      .forEach(
        button => {

          button.addEventListener(
            'click',
            async () => {

              button.disabled =
                true;

              button.textContent =
                'Retrying…';


              try {

                const result =
                  await edge(
                    'razorpayx-create-payout',
                    {
                      payout_id:
                        button.dataset
                          .pn5Retry
                    }
                  );


                await refreshAdmin(
                  host,
                  mentorId,
                  `Safe retry ${
                    result.status ||
                    'submitted'
                  }.`
                );

              }
              catch (error) {

                alert(
                  error?.message ||
                  'Safe retry failed.'
                );


                await refreshAdmin(
                  host,
                  mentorId
                );

              }

            }
          );

        }
      );


    host.querySelectorAll(
      '[data-pn5-reconcile]'
    )
      .forEach(
        button => {

          button.addEventListener(
            'click',
            async () => {

              button.disabled =
                true;

              button.textContent =
                'Reconciling…';


              try {

                const result =
                  await edge(
                    'razorpayx-reconcile-payout',
                    {
                      payout_id:
                        button.dataset
                          .pn5Reconcile
                    }
                  );


                const status =
                  result?.provider
                    ?.status ||
                  result?.applied
                    ?.status ||
                  'updated';


                await refreshAdmin(
                  host,
                  mentorId,
                  `Provider status: ${status}.`
                );

              }
              catch (error) {

                alert(
                  error?.message ||
                  'Reconciliation failed.'
                );


                await refreshAdmin(
                  host,
                  mentorId
                );

              }

            }
          );

        }
      );


    host.querySelectorAll(
      '[data-pn5-cancel]'
    )
      .forEach(
        button => {

          button.addEventListener(
            'click',
            async () => {

              if (
                !window.confirm(
                  'Cancel this unsubmitted payout batch?'
                )
              ) {
                return;
              }


              try {

                await rpc(
                  'pn_admin_cancel_unsubmitted_payout_v1',
                  {
                    p_payout_id:
                      button.dataset
                        .pn5Cancel
                  }
                );


                await refreshAdmin(
                  host,
                  mentorId,
                  'Batch cancelled. Earnings released.'
                );

              }
              catch (error) {

                alert(
                  error?.message ||
                  'Could not cancel payout.'
                );

              }

            }
          );

        }
      );

  }


  async function mountAdmin() {

    if (!activeMentorId) {
      return;
    }


    const detail =
      document.querySelector(
        '[data-pn5-mentor-finance-detail]'
      );


    if (
      !detail ||
      detail.hidden ||
      !detail.querySelector(
        '.pn5-mentor-detail__head'
      )
    ) {
      return;
    }


    if (
      detail.querySelector(
        '[data-pn5-payout-admin]'
      )
    ) {
      return;
    }


    const host =
      document.createElement(
        'section'
      );


    host.className =
      'pn5-payout-admin';


    host.setAttribute(
      'data-pn5-payout-admin',
      ''
    );


    detail.appendChild(
      host
    );


    await refreshAdmin(
      host,
      activeMentorId
    );

  }


  function scheduleAdmin() {

    clearTimeout(
      adminTimer
    );


    adminTimer =
      setTimeout(
        mountAdmin,
        150
      );

  }


  async function mountMentorHistory() {

    if (mentorHistoryBusy) {
      return;
    }


    const view =
      document.querySelector(
        '[data-pnmd-view="earnings"]'
      );


    if (!view) {
      return;
    }


    if (
      view.querySelector(
        '[data-pn5-mentor-payout-history]'
      )
    ) {
      return;
    }


    mentorHistoryBusy =
      true;


    const host =
      document.createElement(
        'article'
      );


    host.className =
      'pnmd-view-card pnmd-view-card--wide pn5-mentor-payout-history';


    host.setAttribute(
      'data-pn5-mentor-payout-history',
      ''
    );


    host.innerHTML = `
      <div class="pn5-payout-empty">
        Loading payout history…
      </div>
    `;


    view.appendChild(
      host
    );


    try {

      const result =
        await rpc(
          'get_my_mentor_payout_history_v1'
        );


      const rows =
        Array.isArray(result)
          ? result
          : [];


      host.innerHTML = `

        <div class="pn5-payout-head">

          <div>
            <span>PAYOUTS</span>
            <h3>Settlement history</h3>
            <p>
              Provider-confirmed mentor payouts.
            </p>
          </div>

          <button
            type="button"
            data-pn5-mentor-history-refresh
          >
            Refresh
          </button>

        </div>


        ${
          rows.length
            ? `
              <div class="pn5-payout-table-wrap">

                <table class="pn5-payout-table">

                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Sessions</th>
                      <th>Provider ID</th>
                      <th>UTR</th>
                    </tr>
                  </thead>

                  <tbody>

                    ${rows
                      .map(
                        row => `
                          <tr>

                            <td>
                              ${esc(
                                dateText(
                                  row.created_at
                                )
                              )}
                            </td>

                            <td>
                              <strong>
                                ${money(
                                  row.amount,
                                  row.currency
                                )}
                              </strong>
                            </td>

                            <td>
                              ${pill(
                                row.status
                              )}
                            </td>

                            <td>
                              ${esc(
                                row.earning_count ||
                                0
                              )}
                            </td>

                            <td>
                              ${esc(
                                row.provider_payout_id ||
                                '—'
                              )}
                            </td>

                            <td>
                              ${esc(
                                row.utr ||
                                '—'
                              )}
                            </td>

                          </tr>
                        `
                      )
                      .join('')}

                  </tbody>

                </table>

              </div>
            `
            : `
              <div class="pn5-payout-empty">
                No payouts yet.
              </div>
            `
        }
      `;


      host.querySelector(
        '[data-pn5-mentor-history-refresh]'
      )
        ?.addEventListener(
          'click',
          () => {

            host.remove();

            setTimeout(
              mountMentorHistory,
              100
            );

          }
        );

    }
    catch (error) {

      host.innerHTML = `
        <div class="pn5-payout-empty pn5-payout-error">
          ${esc(
            error?.message ||
            'Could not load payout history.'
          )}
        </div>
      `;

    }
    finally {

      mentorHistoryBusy =
        false;

    }

  }


  function init() {

    if (!window.PeerNestDB) {

      setTimeout(
        init,
        150
      );

      return;

    }


    mountMentorHistory();


    document.addEventListener(
      'click',
      event => {

        const mentor =
          event.target.closest(
            '[data-pn5-mentor-detail]'
          );


        if (mentor) {

          activeMentorId =
            mentor.dataset
              .pn5MentorDetail ||
            null;


          scheduleAdmin();

        }


        if (
          event.target.closest(
            '[data-pnmd-view-link="earnings"]'
          )
        ) {

          setTimeout(
            mountMentorHistory,
            300
          );

        }

      },
      true
    );


    const observer =
      new MutationObserver(
        () => {

          scheduleAdmin();

        }
      );


    observer.observe(
      document.documentElement,
      {
        attributes:true,
        subtree:true,
        attributeFilter:[
          'data-pnadm-active-operation'
        ]
      }
    );

  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init
    );

  }
  else {

    init();

  }

})();
